import React, { useState, useEffect } from 'react';
import { getGirlUnavailableDates } from '../../utils/bookingValidation';

const CustomDatePicker = ({ 
  selectedDate, 
  onDateSelect, 
  girlId, 
  minDate = new Date().toISOString().split('T')[0],
  disabled = false 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [loading, setLoading] = useState(false);

  // ดึงข้อมูลวันที่ไม่รับงานของน้อง
  useEffect(() => {
    if (girlId) {
      fetchUnavailableDates();
    }
  }, [girlId]);

  const fetchUnavailableDates = async () => {
    try {
      setLoading(true);
      const dates = await getGirlUnavailableDates(girlId);
      setUnavailableDates(dates);
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันสำหรับสร้าง Calendar
  const generateCalendar = (month, year) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const calendar = [];
    const today = new Date();
    
    // เพิ่มวันว่างสำหรับวันแรกของเดือน
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendar.push(null);
    }
    
    // เพิ่มวันในเดือน
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // ใช้ local date string แทน toISOString() เพื่อหลีกเลี่ยง timezone issues
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isPast = dateString < minDate;
      
      // ตรวจสอบว่าวันที่นี้ไม่รับงานหรือไม่
      const isUnavailable = unavailableDates.some(unavailableDate => {
        if (typeof unavailableDate === 'object' && unavailableDate.dateString) {
          // ใช้ dateString ที่แปลงแล้วจาก getGirlUnavailableDates
          return unavailableDate.dateString === dateString;
        } else if (typeof unavailableDate === 'object' && unavailableDate.date) {
          // fallback สำหรับข้อมูลเก่า
          const dateObj = new Date(unavailableDate.date);
          const unavailableDateStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
          return unavailableDateStr === dateString;
        } else if (typeof unavailableDate === 'string') {
          // fallback สำหรับข้อมูลเก่า
          const dateObj = new Date(unavailableDate);
          const unavailableDateStr = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
          return unavailableDateStr === dateString;
        }
        return false;
      });
      
      // ตรวจสอบว่าเป็นวันนี้หรือไม่
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const currentDate = new Date(year, month, day);
      const isToday = currentDate.getTime() === todayDate.getTime();
      
      // ตรวจสอบว่าเป็นวันที่เลือกหรือไม่
      const isSelected = selectedDate === dateString;
      
      calendar.push({
        day,
        date,
        dateString,
        isPast,
        isUnavailable,
        isToday,
        isSelected
      });
    }
    
    return calendar;
  };

  const changeMonth = (direction) => {
    if (direction === -1) {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  const handleDateClick = (dayData) => {
    if (!dayData.isPast && !dayData.isUnavailable && !disabled) {
      onDateSelect(dayData.dateString);
    }
  };

  const monthNames = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  return (
    <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200 shadow-lg max-w-sm mx-auto md:max-w-none md:mx-0">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <button
          type="button"
          onClick={() => changeMonth(-1)}
          className="p-1 md:p-2 hover:bg-gray-100 rounded-lg transition text-lg md:text-base"
          disabled={disabled}
        >
          ←
        </button>
        <div className="flex items-center space-x-2 md:space-x-3">
          <h4 className="text-sm md:text-lg font-semibold">
            {monthNames[currentMonth]} {currentYear}
          </h4>
        </div>
        <div className="flex items-center space-x-1 md:space-x-2">
          <button
            type="button"
            onClick={goToToday}
            className="px-2 md:px-3 py-1 text-xs md:text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition flex items-center"
            disabled={disabled}
          >
            <span className="mr-1 hidden md:inline">📅</span>
            <span className="md:hidden">📅</span>
            <span className="hidden md:inline">วันนี้</span>
          </button>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="p-1 md:p-2 hover:bg-gray-100 rounded-lg transition text-lg md:text-base"
            disabled={disabled}
          >
            →
          </button>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map(day => (
          <div key={day} className="p-1 md:p-2 text-center text-xs md:text-sm font-semibold text-gray-600">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {generateCalendar(currentMonth, currentYear).map((dayData, index) => (
          <div key={index} className="aspect-square">
            {dayData ? (
              <button
                type="button"
                onClick={() => handleDateClick(dayData)}
                disabled={dayData.isPast || dayData.isUnavailable || disabled}
                className={`w-full h-full p-0.5 md:p-1 rounded-lg text-xs md:text-sm font-medium transition ${
                  dayData.isPast
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : dayData.isUnavailable
                    ? 'bg-red-500 text-white cursor-not-allowed'
                    : dayData.isSelected
                    ? 'bg-blue-500 text-white'
                    : dayData.isToday
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                title={
                  dayData.isPast
                    ? 'วันที่ผ่านไปแล้ว'
                    : dayData.isUnavailable
                    ? 'น้องไม่รับงานในวันนี้'
                    : dayData.isToday
                    ? 'วันนี้'
                    : `เลือกวันที่ ${dayData.day}`
                }
              >
                {dayData.day}
              </button>
            ) : (
              <div className="w-full h-full"></div>
            )}
          </div>
        ))}
      </div>
      
      {/* Legend */}
      <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-2 md:space-x-4 text-xs">
          <div className="flex items-center">
            <div className="w-2 h-2 md:w-3 md:h-3 bg-blue-500 rounded mr-1"></div>
            <span className="hidden md:inline">วันนี้</span>
            <span className="md:hidden">วันนี้</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 md:w-3 md:h-3 bg-red-500 rounded mr-1"></div>
            <span className="hidden md:inline">ไม่รับงาน</span>
            <span className="md:hidden">ไม่รับ</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 md:w-3 md:h-3 bg-gray-50 border border-gray-300 rounded mr-1"></div>
            <span className="hidden md:inline">รับงานปกติ</span>
            <span className="md:hidden">รับงาน</span>
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 md:w-3 md:h-3 bg-gray-100 rounded mr-1"></div>
            <span className="hidden md:inline">วันที่ผ่านไปแล้ว</span>
            <span className="md:hidden">ผ่านแล้ว</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDatePicker;
